import { isFeatureEnabled } from "../../constant/featureVisibility";
import axios from "axios";
import SimilaritySearch from "./SimilaritySearch";
import { useDataContext } from "../../utils/DataContext";
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import DAGSelector from "../../components/DAGSelector/DAGSelector";
import { isDagSelectionValid, normalizeDagCatalog } from "../../utils/dagCatalog";
import Timer from "../../components/Timer";
import "../../styles/org-buttons.css";
import StatusCheckPage from "./pages/LocalModelStatus";
import SearchableSelect from "../../components/SearchableSelect/SearchableSelect";
import "./LocalInferencePage.css";
import { buttonText } from "../../utils/buttonText";
import { pageText } from "../../utils/pageText";

function LocalInferenceContent(props) {
  const { item, loadDatasetList } = props;

  const [dinoModel, setDinoModel] = useState("dinov2");
  const [folderLabels, setFolderLabels] = useState(false);
  const [mode, setMode] = useState("autodag");
  const [ovdModel, setOvdModel] = useState("groundingdino");
  const [frMode, setFrMode] = useState("nir");
  const [dagname, setDagname] = useState(null);
  const [subDag, setSubDag] = useState("");
  const [showDagPicker, setShowDagPicker] = useState(false);
  const [labelTags, setLabelTags] = useState([]);
  const [labelInput, setLabelInput] = useState("");
  const [tasksStatus, setTasksStatus] = useState();
  const [loading, setLoading] = useState(false);
  const [showStatusCheckModal, setShowStatusCheckModal] = useState(false);
  const [statusRequest, setStatusRequest] = useState(0);
  const refreshedTasks = useRef(new Set());

  useEffect(() => {
    refreshedTasks.current.clear();
    setTasksStatus(undefined);
  }, [item.name]);

  useEffect(() => {
    if (!showStatusCheckModal) return;
    let cancelled = false;
    let timer;
    const checkStatus = async () => {
      try {
        const { data } = await axios.get(
          `/models/docker/status?dataset_name=${encodeURIComponent(item.name)}`);
        if (cancelled) return;
        setTasksStatus(data);
        const entries = Object.entries(data || {});
        let needsRefresh = false;
        entries.forEach(([key, task]) => {
          const id = task.prelabel_id || task.rq_id || key;
          if (["finished", "succeeded"].includes(task.status?.toLowerCase()) &&
              !refreshedTasks.current.has(id)) {
            refreshedTasks.current.add(id);
            needsRefresh = true;
          }
        });
        // Keep the cards mounted, including this task's completion history.
        if (needsRefresh) await loadDatasetList({ silent: true });
        const terminal = ["finished", "succeeded", "failed", "no_detections",
          "save-error", "canceled", "cancelled", "stopped"];
        if (!cancelled && entries.some(([, task]) =>
          !terminal.includes(task.status?.toLowerCase()))) {
          timer = setTimeout(checkStatus, 3000);
        }
      } catch (error) {
        if (!cancelled) window.alert(error.response?.data || "Unable to load task status");
      }
    };
    checkStatus();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showStatusCheckModal, statusRequest, item.name, loadDatasetList]);

  const { autodagNames, autodagItems, dagScanInfo, dagLoading, dagError, refreshDags } = useDataContext();
  const dagItems = useMemo(() => autodagItems || normalizeDagCatalog(autodagNames).autodagItems,
    [autodagItems, autodagNames]);
  const validDag = isDagSelectionValid(dagItems, dagname, subDag);

  useEffect(() => {
    if (!dagname) return;
    const selected = dagItems.find((entry) => entry.path === dagname);
    if (!selected) { setDagname(null); setSubDag(""); }
    else if (!isDagSelectionValid(dagItems, dagname, subDag)) {
      setSubDag(selected.sub_dags.length === 1 ? selected.sub_dags[0] : "");
    }
  }, [dagItems, dagname, subDag]);

  const hasLabel = item.sample_label_fields && item.sample_label_fields.length > 0;

  const addTag = (val) => {
    const trimmed = val.trim().replace(/,$/, "");
    if (trimmed && !labelTags.includes(trimmed)) {
      setLabelTags((prev) => [...prev, trimmed]);
    }
    setLabelInput("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(labelInput);
    } else if (e.key === "Backspace" && !labelInput) {
      setLabelTags((prev) => prev.slice(0, -1));
    }
  };

  const handleTagInputBlur = () => {
    if (labelInput.trim()) addTag(labelInput);
  };

  const handleStart = async () => {
    let url;
    if (mode === "dino") {
      url = `/models/dino/extract_features?dataset_name=${encodeURIComponent(item.name)}&model=${dinoModel}${folderLabels ? "&labels_from_folders=true" : ""}`;
    } else if (mode === "autodag") {
      if (!validDag) {
        window.alert(pageText("Choose a DAG and sub DAG first"));
        return;
      }
      url = `/models/docker/autodag/load_model?dataset_name=${encodeURIComponent(item.name)}&dag_name=${encodeURIComponent(dagname)}&sub_dag=${subDag}`;
    } else if (mode === "ovd") {
      if (labelTags.length === 0) {
        window.alert("Please add at least one label.");
        return;
      }
      url = `/models/${ovdModel}/load_model?dataset_name=${encodeURIComponent(item.name)}&labels=${encodeURIComponent(labelTags.join(","))}`;
    } else {
      url = `/models/fr/load_model?dataset_name=${encodeURIComponent(item.name)}&fr_mode=${encodeURIComponent(frMode)}`;
    }
    setLoading(true);
    axios
      .put(url)
      .then(() => {
        setLoading(false);
        handleStatusCheck();
      })
      .catch((error) => {
        setLoading(false);
        window.alert(error.response?.data?.error || error.response?.data || "Unknown error");
      });
  };

  const labelStatusClass = hasLabel ? "status-ready" : "status-empty";

  const handleStatusCheck = () => {
    setStatusRequest((value) => value + 1);
    setShowStatusCheckModal(true);
  };

  return (
    <div className="model-customized-content">
      <div className="model-info-badges">
        <span className={`badge-label ${labelStatusClass}`}>
          {pageText("Labels:")} {pageText(hasLabel ? "Yes" : "No")}
        </span>
        <span className="badge-samples">{pageText("Samples:")} {item.samples?.length || 0}</span>
      </div>

      <div className="prelabel-card-section">
        <span>{pageText("Model Mode")}</span>
        <div className="mode-toggle">
          <button
            type="button"
            className={`mode-btn ${mode === "autodag" ? "active" : ""}`}
            onClick={() => setMode("autodag")}
          >
            {buttonText("DAGs")}
          </button>
          <button
            type="button"
            className={`mode-btn ${mode === "ovd" ? "active" : ""}`}
            onClick={() => setMode("ovd")}
            title="Open-Vocabulary Object Detection"
          >
            OVD
          </button>
          <button type="button" className={`mode-btn ${mode === "dino" ? "active" : ""}`}
            onClick={() => setMode("dino")}>DINO</button>
          <button
            type="button"
            hidden={!isFeatureEnabled("faceRecognition")} className={`mode-btn ${mode === "fr" ? "active" : ""}`}
            onClick={() => setMode("fr")}
          >
            {buttonText("FR")}
          </button>
        </div>
      </div>

      {mode === "ovd" && <div className="prelabel-card-section">
        <label htmlFor={`ovd-model-${item.name}`}>OVD Model</label>
        <select id={`ovd-model-${item.name}`} value={ovdModel}
          onChange={event => setOvdModel(event.target.value)}>
          <option value="groundingdino">GroundingDINO</option>
          <option value="yoloworld">YOLO-World</option>
        </select>
      </div>}

      {mode === "dino" && <div className="prelabel-card-section">
        <label htmlFor={`dino-model-${item.name}`}>DINO Model</label>
        <select id={`dino-model-${item.name}`} value={dinoModel}
          onChange={event => setDinoModel(event.target.value)}>
          <option value="dinov2">DINOv2 Large</option>
          <option value="dinov2_base">DINOv2 ViT-B/14</option>
          <option value="dinov3">DINOv3 ViT-B/16</option>
        </select>
        <label><input type="checkbox" checked={folderLabels} onChange={event => setFolderLabels(event.target.checked)} />
          Use parent folder names as classification labels</label>
        {folderLabels && <small>Save labels to ground_truth; existing labels are preserved.</small>}
        <span>{pageText("Embedding")}</span>
        <SimilaritySearch key={`${item.name}:${dinoModel}`} datasetName={item.name} model={dinoModel} />
      </div>}

      {mode === "autodag" && (
        <div className="model-select-wrapper prelabel-card-section">
          <span>{pageText("DAG Selection")}</span>
          <button type="button" className="org-btn org-btn-secondary"
            onClick={() => setShowDagPicker(true)}>{pageText("Choose DAG")}</button>
          {dagname && <div className="dag-selection-preview">
            <strong>{dagname}</strong>
            <span>{validDag ? `DAG${subDag}` : pageText("Choose a sub DAG")}</span>
          </div>}
        </div>
      )}

      {isFeatureEnabled("faceRecognition") && mode === "fr" && (
        <div className="model-select-wrapper prelabel-card-section">
          <span>{pageText("FR Mode")}</span>
          <SearchableSelect
            options={["nir", "rgb"]}
            value={frMode}
            onChange={setFrMode}
            placeholder="Please choose FR mode"
          />
        </div>
      )}

      {mode === "ovd" && (
        <div className="tag-input-wrapper prelabel-card-section">
          <span>{pageText("Prompt Labels")}</span>
          <div className="tag-input-box">
            {labelTags.map((tag) => (
              <span key={tag} className="label-tag">
                {tag}
                <button
                  className="tag-remove"
                  onClick={() => setLabelTags((prev) => prev.filter((t) => t !== tag))}
                >
                  ×
                </button>
              </span>
            ))}
            <input
              className="tag-text-input"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              onBlur={handleTagInputBlur}
              placeholder={labelTags.length === 0 ? pageText("Type label + Enter") : ""}
            />
          </div>
        </div>
      )}

      <div className="model-button-group">
        <button
          type="button"
          className="org-btn org-btn-primary"
          onClick={handleStart}
          disabled={loading || (mode === "autodag" && (!validDag || dagLoading))}
        >
          {buttonText(loading ? <Timer loadingStates={[loading]} /> : "Start")}
        </button>
        <button
          type="button"
          className="org-btn org-btn-secondary"
          onClick={handleStatusCheck}
        >
          {buttonText("Status")}
        </button>
      </div>

      <Modal show={showDagPicker} onHide={() => setShowDagPicker(false)} size="lg" centered scrollable
        className="dataset-enterprise-modal" aria-label={pageText("Choose DAG")}>
        <Modal.Header closeButton closeLabel={buttonText("Close")} className="dataset-modal-header">
          <Modal.Title>{pageText("Choose DAG")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="dataset-modal-body">
          <DAGSelector items={dagItems} value={dagname} subDag={subDag}
            onChange={(path, branch) => { setDagname(path); setSubDag(branch); }}
            onRefresh={refreshDags} loading={dagLoading} error={dagError} scan={dagScanInfo} />
        </Modal.Body>
        <Modal.Footer className="org-modal-footer">
          <button type="button" className="org-btn org-btn-primary" disabled={!validDag || dagLoading}
            onClick={() => setShowDagPicker(false)}>{buttonText("Confirm")}</button>
        </Modal.Footer>
      </Modal>
      <StatusCheckPage
        show={showStatusCheckModal}
        onHide={() => setShowStatusCheckModal(false)}
        reloadStatus={handleStatusCheck}
        tasksStatus={tasksStatus}
      />
    </div>
  );
}

export default LocalInferenceContent;
